import {
  feedbackcard,
  Field,
  RelationClass as RelationClassType,
  SortOption,
  YesObject
} from '@yes.technology/react-toolkit'
import { useObjectclass } from 'objectclass/shared'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useLocation } from 'react-router-dom'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import { FieldSettings } from 'shared/types'
import { updateDisplayText } from 'shared/utils'
import deepCopy from 'shared/utils/object/deepCopy'
import DataTable from 'site/Renderer/components/DataTables'
import { DataTableConfigProps } from 'site/Renderer/components/DataTables/DataTable'
import useChangedFieldValues from 'site/Renderer/components/DataTables/hooks/useChangedFieldValues'
import useTableLayouts from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import useTableMode from 'site/Renderer/components/DataTables/hooks/useTableMode'
import {
  ExtraSelectionAction,
  SelectAndLinkOptions,
  SelectionAction
} from 'site/Renderer/components/DataTables/types'
import useLocationState from 'site/Renderer/components/FilterFields/hooks/useLocationState'
import { useSiteState } from 'siteState/shared'
import { useFetchYesObjects } from 'yesObject/shared'
import getRelationCounterpartField from '../../lib/getRelationCountertpartField/getRelationCounterpartField'

type RelationClassProps = DataTableConfigProps & {
  relationFieldRelation?: RelationClassType
  fieldValues?: YesObject
  field?: Field
  informational?: boolean
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  fieldSettings?: FieldSettings
  hideInsertAction?: boolean
  hideSelectAndLinkAction?: boolean
  selectAndLinkTargetPath?: string
  onChange: (fieldColumn: string, value: YesObject[string]) => void
  save: (fieldToSave: string) => Promise<void>
  changeAndSave: (
    fieldColumn: string,
    value: YesObject[string]
  ) => Promise<void>
}

const RelationClass = ({
  relationFieldRelation,
  fieldValues,
  layoutClassificationUuid,
  hierarchyLevel,
  actionButton,
  field,
  informational,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  fieldSettings,
  hideInsertAction = false,
  hideSelectAndLinkAction = false,
  selectAndLinkTargetPath,
  onChange,
  save,
  changeAndSave
}: RelationClassProps) => {
  const { t } = useTranslation()

  const objectclassUuid = relationFieldRelation?.uuid_objectclass || ''

  const { objectclass } = useObjectclass({ objectclassUuid })

  const { fields = {} } = objectclass || {}

  const updatedFields = useMemo(
    () => updateDisplayText(fields, fieldSettings),
    [fieldSettings, fields]
  )

  const columns = useMemo(() => Object.keys(updatedFields), [updatedFields])

  const [tableLayout] = useTableLayouts({ layoutClassificationUuid })

  const orderBy = tableLayout.defaultOrderBy.map(
    ([key, value]) => ({ [key]: value }) as SortOption
  )

  const relationCounterpartField = getRelationCounterpartField({
    relationFieldRelation,
    fieldValues
  })

  const [relationCounterpartColumn] = Object.keys(
    relationCounterpartField || {}
  )

  const {
    yesObjects,
    isFetching,
    pagination,
    totalObjects,
    setPage,
    sortOptions,
    setSortOptions,
    fetchYesObjects: refetchObjects
  } = useFetchYesObjects({
    filterBody: {
      query: {
        $and: [
          {
            [`${relationCounterpartColumn}.uuid`]: {
              $in: [`${fieldValues?.uuid}`]
            },
            'situation.des': {
              $gte: '40'
            }
          }
        ]
      },
      objectclass: objectclassUuid
    },
    enabled: !!relationCounterpartColumn && !!objectclassUuid
  })

  const prevOrderBy = useRef({})

  useEffect(() => {
    if (JSON.stringify(prevOrderBy.current) !== JSON.stringify(orderBy)) {
      setSortOptions(orderBy)
      prevOrderBy.current = orderBy
    }
  }, [orderBy, setSortOptions])

  const { persistentSelection, targetField } = useLocationState()

  const currentField = field?.column_json || ''
  const currentFieldPersistentSelection =
    persistentSelection?.ObjectView?.[currentField]

  const unsavedObjectsUuids =
    currentFieldPersistentSelection?.map(
      (persistentSelection) => persistentSelection?.uuid
    ) || []

  const [_, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  const id = useId()

  const relationId = targetField || id

  const tableId = field?.column_json || id

  const onScrollToRelationTable = useCallback(() => {
    const relationTable = document.getElementById(relationId)

    if (relationTable) {
      relationTable.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [relationId])

  const selectAndLinkOptions: SelectAndLinkOptions = useMemo(
    () => ({
      targetComponent: 'ObjectView',
      targetField: currentField,
      selectedOptions: currentFieldPersistentSelection
    }),
    [currentField, currentFieldPersistentSelection]
  )

  const [unsavedObjects, setUnsavedObjects] = useState<YesObject[]>([])

  const navigateMergingState = useNavigateMergingState()
  const location = useLocation()

  useEffect(() => {
    if (!currentFieldPersistentSelection) {
      setUnsavedObjects([])
      return
    }

    setUnsavedObjects((unsavedObjects) => {
      const objectsToLink = currentFieldPersistentSelection.filter(
        (objectToLink) =>
          yesObjects.every(
            (savedObject) => savedObject.uuid !== objectToLink.uuid
          )
      )

      if (!objectsToLink.length && !unsavedObjects.length) return unsavedObjects

      onChange(currentField, [...yesObjects, ...objectsToLink])

      return objectsToLink
    })
  }, [currentField, currentFieldPersistentSelection, onChange, yesObjects])

  useEffect(() => {
    if (!currentField || !currentFieldPersistentSelection) return

    const wereObjectsLinked = currentFieldPersistentSelection.every(
      (objectToLink) =>
        yesObjects.some((savedObject) => savedObject.uuid === objectToLink.uuid)
    )

    if (!wereObjectsLinked) return

    const newPersistentSelection = deepCopy(persistentSelection)
    delete newPersistentSelection.ObjectView?.[currentField]

    navigateMergingState('.' + location.search, {
      state: {
        ...location.state,
        persistentSelection: newPersistentSelection,
        targetComponent: undefined,
        targetField: undefined
      },
      replace: true
    })

    onScrollToRelationTable()
  }, [
    currentField,
    currentFieldPersistentSelection,
    location.search,
    location.state,
    navigateMergingState,
    persistentSelection,
    yesObjects,
    onScrollToRelationTable
  ])

  useEffect(() => {
    if (isFetching || !unsavedObjects.length) return

    onScrollToRelationTable()
  }, [unsavedObjects, isFetching, onScrollToRelationTable])

  const tableObjects = useMemo(
    () => [...yesObjects, ...unsavedObjects],
    [yesObjects, unsavedObjects]
  )

  const handleSave = () => {
    save(currentField).then(() => refetchObjects())
  }

  const saveSelectionAction: SelectionAction = {
    children: t('data-tables.save'),
    onClick: handleSave,
    impactClass: 'situationChange',
    pulseAnimate: 'default'
  }

  const unlinkObjects = async (objects: YesObject[]) => {
    if (!field?.column_json) return

    setLockObjectViewObject(true)

    const { savedRelations, unsavedRelations } = objects.reduce(
      (
        acc: { savedRelations: YesObject[]; unsavedRelations: YesObject[] },
        object
      ) => {
        if (yesObjects.some((yesObject) => yesObject.uuid === object.uuid)) {
          acc.savedRelations.push(object)
        } else {
          acc.unsavedRelations.push(object)
        }

        return acc
      },
      {
        savedRelations: [],
        unsavedRelations: []
      }
    )

    if (savedRelations.length > 0) {
      changeAndSave(
        field.column_json,
        yesObjects.filter((yesObject) => {
          return !savedRelations.some(
            (object) => object.uuid === yesObject.uuid
          )
        })
      )
    }

    if (
      unsavedRelations.length > 0 &&
      persistentSelection?.ObjectView?.[currentField]
    ) {
      const newPersistentSelection = deepCopy(persistentSelection)

      newPersistentSelection.ObjectView =
        newPersistentSelection.ObjectView || {}

      newPersistentSelection.ObjectView = {
        ...newPersistentSelection.ObjectView,
        [currentField]: newPersistentSelection.ObjectView[currentField]?.filter(
          (persistentSelection) =>
            !unsavedRelations.some(
              (object) => object.uuid === persistentSelection?.uuid
            )
        )
      }

      navigateMergingState('.' + location.search, {
        state: {
          ...location.state,
          persistentSelection: newPersistentSelection
        },
        replace: true
      })

      if (savedRelations.length === 0) {
        feedbackcard(t('data-tables.success'), {
          type: 'success',
          message: t('data-tables.success')
        })
      }
    }
  }

  const primarySelectionAction = unsavedObjects.length
    ? saveSelectionAction
    : undefined

  const extraSelectionActions: ExtraSelectionAction[] = hideSelectAndLinkAction
    ? []
    : [
        {
          children: t('data-tables.unlink'),
          onClick: unlinkObjects,
          impactClass: 'situationChange'
        }
      ]

  return (
    <DataTable
      informational={informational}
      embedded={true}
      idObjectClass={objectclassUuid}
      objects={tableObjects}
      onSort={setSortOptions}
      setCurrentPage={setPage}
      actionColumnsPosition='last'
      title={field?.display_text.des}
      displayLayoutSelection={false}
      hideEditAction
      disableLinkButton
      insertOnly={tableObjects.length === 0}
      highlightedRows={unsavedObjectsUuids}
      {...{
        objectclass,
        columns,
        fields: updatedFields,
        isLoading: isFetching,
        pagination,
        totalObjects,
        sortOptions,
        actionButton,
        hierarchyLevel,
        refetchObjects,
        relationCounterpartField,
        navigationSliderBackgroundColor,
        navigationSliderColor,
        fieldSettings,
        selectAndLinkOptions,
        primarySelectionAction,
        extraSelectionActions,
        tableLayout,
        hideInsertAction,
        hideSelectAndLinkAction,
        selectAndLinkTargetPath,
        id: tableId
      }}
      {...useTableMode()}
      {...useChangedFieldValues()}
    />
  )
}

export default RelationClass
